import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Calculator, ChevronDown, CreditCard, FileCheck2, Landmark, Lock, Mail, Phone, RefreshCw, ShieldCheck } from 'lucide-react';
import { Slider } from './ui/slider';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from './ui/accordion';
import { KonditionenTabelle, LAUFZEITEN, MIN_GESAMT, Zahlungsplan, eur } from './RatenShared';

export const SLIDER_MAX = 25000;
const sliderCls = '[&_[data-orientation=horizontal]]:h-2 [&_[data-orientation=horizontal]>span]:bg-[#b8893d] [&_[role=slider]]:h-6 [&_[role=slider]]:w-6 [&_[role=slider]]:border-2 [&_[role=slider]]:border-[#b8893d] [&_[role=slider]]:bg-white [&_[role=slider]]:shadow-[0_0_0_6px_rgba(184,137,61,0.15)] [&_[role=slider]]:transition-shadow [&_[role=slider]:hover]:shadow-[0_0_0_10px_rgba(184,137,61,0.22)] [&_[role=slider]]:cursor-grab';

export const StepCard = ({ nr, titel, text, children, testid }) => (
  <section className="bg-white border border-gray-100 rounded-sm shadow-[0_1px_2px_rgba(27,58,92,0.06)] p-6 sm:p-8" data-testid={testid}>
    <div className="flex items-start gap-4 mb-6">
      <span className="shrink-0 w-9 h-9 rounded-full bg-[#1B3A5C] text-[#e0b56a] font-serif text-lg flex items-center justify-center">{nr}</span>
      <div>
        <h2 className="font-serif text-xl md:text-2xl text-[#1B3A5C] leading-tight">{titel}</h2>
        {text && <p className="text-sm text-[#7a8a99] mt-1">{text}</p>}
      </div>
    </div>
    {children}
  </section>
);

export const BetragRegler = ({ value, onChange, testid = 'raten-info-betrag-slider' }) => {
  const { t } = useTranslation();
  const v = Math.min(Math.max(Number(value) || MIN_GESAMT, MIN_GESAMT), SLIDER_MAX);
  return (
    <div className="mt-5" data-testid={testid}>
      <Slider min={MIN_GESAMT} max={SLIDER_MAX} step={50} value={[v]} onValueChange={([x]) => onChange(x)} className={sliderCls} aria-label={t('raten.info.schritt_1_titel')} />
      <div className="flex justify-between text-[11px] text-[#7a8a99] mt-2"><span>{t('raten.info.betrag_min')}</span><span>{t('raten.info.betrag_max')}</span></div>
      {Number(value) > SLIDER_MAX && <p className="text-[11px] text-[#b8893d] mt-1">{t('raten.info.betrag_hoeher')}</p>}
    </div>
  );
};

export const LaufzeitRegler = ({ value, onChange, testid = 'raten-info-laufzeit-slider' }) => {
  const { t } = useTranslation();
  const idx = Math.max(LAUFZEITEN.indexOf(value), 0);
  return (
    <div className="mt-4" data-testid={testid}>
      <Slider min={0} max={LAUFZEITEN.length - 1} step={1} value={[idx]} onValueChange={([i]) => onChange(LAUFZEITEN[i])} className={sliderCls} aria-label={t('raten.info.laufzeit_regler')} />
      <div className="flex justify-between text-[11px] text-[#7a8a99] mt-2">{LAUFZEITEN.map((m) => <span key={m} className={m === value ? 'text-[#1B3A5C] font-semibold' : ''}>{t('raten.info.monate_kurz', { count: m })}</span>)}</div>
    </div>
  );
};

const Metrik = ({ label, wert, testid }) => (
  <div className="bg-white/[0.06] border border-white/10 rounded-sm px-3 py-2.5" data-testid={testid}>
    <div className="text-[10px] tracking-wider uppercase text-white/55">{label}</div>
    <div className="text-sm font-semibold text-white mt-0.5">{wert}</div>
  </div>
);

const useFlash = (dep) => {
  const [flash, setFlash] = useState(false);
  const first = useRef(true);
  useEffect(() => {
    if (first.current) { first.current = false; return undefined; }
    setFlash(true);
    const h = setTimeout(() => setFlash(false), 500);
    return () => clearTimeout(h);
  }, [dep]);
  return flash;
};

export const ErgebnisPanel = ({ vorschau, laufzeit, panelRef, raten, kicker, disclaimer = true, defaultOffen = false }) => {
  const { t } = useTranslation();
  const [offen, setOffen] = useState(defaultOffen);
  const flash = useFlash(vorschau?.rate);
  return (
    <div ref={panelRef} className="bg-[#1B3A5C] text-white rounded-sm p-6 lg:p-8 relative overflow-hidden scroll-mt-44 lg:scroll-mt-40" data-testid="raten-info-ergebnis">
      <div className="absolute -top-10 -right-10 w-48 h-48 bg-[#b8893d] rounded-full blur-3xl opacity-25 pointer-events-none"></div>
      <div className="relative">
        <div className="text-[11px] tracking-[0.28em] text-[#e0b56a] uppercase mb-4">{kicker || t('raten.info.vorschau_kicker')}</div>
        {vorschau ? (
          <>
            <div className="flex items-end gap-3 flex-wrap">
              <div className={`font-serif text-5xl md:text-6xl leading-none transition-all duration-500 ${flash ? 'text-[#e0b56a] scale-[1.03]' : 'text-white'}`} style={{ transformOrigin: 'left bottom' }} data-testid="raten-info-rate">{eur(vorschau.rate)}</div>
              <div className="text-white/70 text-sm pb-1">{t('raten.info.pro_monat', { count: vorschau.anzahl_raten ?? laufzeit })}</div>
            </div>
            <div className="grid grid-cols-2 gap-2 mt-6">
              <Metrik label={t('raten.info.metrik_anzahlung')} wert={eur(vorschau.anzahlung)} testid="raten-info-metrik-anzahlung" />
              <Metrik label={t('raten.info.metrik_gebuehr')} wert={vorschau.zinsfrei ? t('raten.kond.zinsfrei_badge') : eur(vorschau.bearbeitungsgebuehr)} testid="raten-info-metrik-gebuehr" />
              <Metrik label={t('raten.info.metrik_eff')} wert={`${vorschau.effektiver_jahreszins.toFixed(2).replace('.', ',')} %`} testid="raten-info-metrik-eff" />
              <Metrik label={t('raten.info.metrik_gesamt')} wert={eur(vorschau.gesamtkosten)} testid="raten-info-metrik-gesamt" />
            </div>
            <button type="button" onClick={() => setOffen((o) => !o)} aria-expanded={offen} data-testid="raten-info-details-toggle" className="mt-5 w-full flex items-center justify-between gap-3 text-xs tracking-[0.15em] uppercase text-[#e0b56a] border border-[#e0b56a]/40 hover:border-[#e0b56a] hover:bg-white/5 rounded-sm px-4 py-3 transition-colors">
              <span>{offen ? t('raten.info.details_verbergen') : t('raten.info.details_zeigen')}</span>
              <ChevronDown className={`w-4 h-4 transition-transform duration-300 ${offen ? 'rotate-180' : ''}`} />
            </button>
            <div className={`grid transition-[grid-template-rows] duration-300 ease-out ${offen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}`}>
              <div className="overflow-hidden">
                <div className="mt-4 bg-white/5 rounded-sm p-3 [&_td]:!text-white/80 [&_td.font-semibold]:!text-white [&_th]:!text-white/60 [&_tr]:!border-white/10" data-testid="raten-info-details">
                  <KonditionenTabelle b={vorschau} compact testid="raten-info-konditionen" />
                  <div className="mt-4"><Zahlungsplan b={vorschau} raten={raten} compact testid="raten-info-zahlungsplan" /></div>
                </div>
              </div>
            </div>
          </>
        ) : (
          <div className="py-6" data-testid="raten-info-leer">
            <Calculator className="w-8 h-8 text-[#e0b56a]/70 mb-3" />
            <div className="font-serif text-2xl">{t('raten.info.leer_titel')}</div>
            <p className="text-sm text-white/65 mt-2">{t('raten.info.leer_text')}</p>
          </div>
        )}
        {disclaimer && <div className="mt-5 text-[10px] text-white/50 leading-relaxed">{t('raten.info.disclaimer')}</div>}
      </div>
    </div>
  );
};

export const MobileRateBar = ({ vorschau, laufzeit, onDetails }) => {
  const { t } = useTranslation();
  const [top, setTop] = useState(104);
  useEffect(() => {
    const mess = () => setTop(document.querySelector('header')?.getBoundingClientRect().height || 104);
    mess();
    window.addEventListener('scroll', mess, { passive: true });
    window.addEventListener('resize', mess);
    return () => { window.removeEventListener('scroll', mess); window.removeEventListener('resize', mess); };
  }, []);
  if (!vorschau) return null;
  return (
    <div style={{ top }} className="lg:hidden sticky z-30 -mx-6 mb-4 bg-[#1B3A5C]/95 backdrop-blur-md border-b border-[#e0b56a]/30 text-white px-6 py-3 flex items-center justify-between gap-3 shadow-lg" data-testid="raten-info-mobile-bar">
      <div>
        <div className="text-[10px] tracking-[0.2em] uppercase text-[#e0b56a]">{t('raten.info.mobile_rate')}</div>
        <div className="font-serif text-2xl leading-none mt-0.5" data-testid="raten-info-mobile-rate">{eur(vorschau.rate)} <span className="text-xs text-white/60 font-sans">{t('raten.info.pro_monat', { count: vorschau.anzahl_raten ?? laufzeit })}</span></div>
      </div>
      <button type="button" onClick={onDetails} data-testid="raten-info-mobile-details" className="text-[11px] tracking-[0.15em] uppercase border border-[#e0b56a] text-[#e0b56a] px-4 py-2 rounded-sm">{t('raten.info.mobile_details')}</button>
    </div>
  );
};

const ABLAUF_ICONS = [Calculator, FileCheck2, CreditCard, RefreshCw];

export const AblaufLeiste = () => {
  const { t } = useTranslation();
  return (
    <section className="max-w-6xl mx-auto px-6 lg:px-10 mt-16" data-testid="raten-info-ablauf">
      <div className="text-[11px] tracking-[0.28em] text-[#b8893d] uppercase mb-2">{t('raten.info.ablauf_kicker')}</div>
      <h2 className="font-serif text-2xl md:text-3xl text-[#1B3A5C] mb-8">{t('raten.info.ablauf_titel')}</h2>
      <ol className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 relative">
        <div className="hidden lg:block absolute top-6 left-[12%] right-[12%] h-px bg-gradient-to-r from-[#b8893d]/20 via-[#b8893d]/60 to-[#b8893d]/20" />
        {ABLAUF_ICONS.map((Icon, i) => (
          <li key={i} className="relative" data-testid={`raten-info-ablauf-${i + 1}`}>
            <div className="flex items-center gap-3 lg:flex-col lg:items-start">
              <span className="relative z-10 w-12 h-12 rounded-full bg-white border-2 border-[#b8893d] text-[#1B3A5C] flex items-center justify-center shrink-0"><Icon className="w-5 h-5" /></span>
              <div className="lg:mt-4">
                <div className="text-[10px] tracking-[0.25em] text-[#b8893d] uppercase">0{i + 1}</div>
                <div className="font-medium text-[#1B3A5C]">{t(`raten.info.ablauf_s${i + 1}`)}</div>
              </div>
            </div>
            <p className="text-xs text-[#7a8a99] leading-relaxed mt-2 lg:mt-3">{t(`raten.info.ablauf_${i + 1}`)}</p>
          </li>
        ))}
      </ol>
    </section>
  );
};

const FAQ_KEYS = ['kosten', 'sepa', 'bonitaet', 'widerruf', 'verzug', 'vorzeitig'];

export const RatenFaq = () => {
  const { t } = useTranslation();
  return (
    <section className="max-w-6xl mx-auto px-6 lg:px-10 mt-16 grid grid-cols-1 lg:grid-cols-5 gap-8" data-testid="raten-info-faq">
      <div className="lg:col-span-2">
        <div className="text-[11px] tracking-[0.28em] text-[#b8893d] uppercase mb-2">{t('raten.info.faq_kicker')}</div>
        <h2 className="font-serif text-2xl md:text-3xl text-[#1B3A5C]">{t('raten.info.faq_titel')}</h2>
      </div>
      <Accordion type="single" collapsible className="lg:col-span-3 bg-white border border-gray-100 rounded-sm px-6">
        {FAQ_KEYS.map((k) => (
          <AccordionItem key={k} value={k} className="border-gray-100" data-testid={`raten-info-faq-${k}`}>
            <AccordionTrigger className="text-left text-sm font-medium text-[#1B3A5C] hover:no-underline hover:text-[#b8893d] py-4">{t(`raten.info.faq.${k}.q`)}</AccordionTrigger>
            <AccordionContent className="text-sm text-[#34536b] leading-relaxed">{t(`raten.info.faq.${k}.a`)}</AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </section>
  );
};

export const TrustStrip = () => {
  const { t } = useTranslation();
  const items = [
    { k: 'stripe', Icon: ShieldCheck },
    { k: 'dsgvo', Icon: Lock },
    { k: 'kontakt', Icon: Landmark },
  ];
  return (
    <section className="max-w-6xl mx-auto px-6 lg:px-10 mt-16" data-testid="raten-info-trust">
      <div className="text-[11px] tracking-[0.28em] text-[#b8893d] uppercase mb-5">{t('raten.info.trust_kicker')}</div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {items.map(({ k, Icon }) => (
          <div key={k} className="bg-white border border-gray-100 rounded-sm p-5 flex gap-4" data-testid={`raten-info-trust-${k}`}>
            <span className="w-10 h-10 rounded-full bg-[#f9f4ec] text-[#b8893d] flex items-center justify-center shrink-0"><Icon className="w-5 h-5" /></span>
            <div>
              <div className="text-sm font-medium text-[#1B3A5C]">{t(`raten.info.trust.${k}.titel`)}</div>
              <p className="text-xs text-[#7a8a99] mt-1 leading-relaxed">{t(`raten.info.trust.${k}.text`)}</p>
              {k === 'kontakt' && (
                <div className="mt-3 space-y-1 text-xs">
                  <a href="tel:+492283040640" className="flex items-center gap-2 text-[#1B3A5C] hover:text-[#b8893d]" data-testid="raten-info-trust-tel"><Phone className="w-3.5 h-3.5" />+49 (0) 228 30 40 640</a>
                  <a href="mailto:bonn@kanzlei-partner.com" className="flex items-center gap-2 text-[#1B3A5C] hover:text-[#b8893d]" data-testid="raten-info-trust-mail"><Mail className="w-3.5 h-3.5" />bonn@kanzlei-partner.com</a>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};
